import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ProfileSelector from 'app/components/ProfileSelector';
import { Page } from 'app/modules/insights/types/Page';
import useInsightsSelectorFilter from 'app/modules/insights/components/insightsPageSelector/hooks/useInsightsSelectorFilter';
import InsightsPageSelectorOptions from 'app/modules/insights/components/insightsPageSelector/components/InsightsPageSelectorOptions';
import getProfileSelectorTitleByFilterOption from 'app/components/profileSelector/utils/getProfileSelectorTitleByFilterOption';
import { useActions } from 'app/hooks/useActions';
import { ActionButton } from 'app/components/actions/ActionButton';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';

type Props = {
  pages: Page[];
  selectedPageId: string;
  onSelectPage: (id: string) => void;
};

const InsightsLitePageSelector: FC<Props> = ({
  pages,
  selectedPageId,
  onSelectPage,
}) => {
  const { t } = useTranslation();
  const {
    searchQuery,
    filteredPages,
    setSearchQuery,
    selectFilterOption,
    sidebarFilterOptions,
    selectedFilterOption,
  } = useInsightsSelectorFilter(pages);
  const socialLogin = useSocialLoginContext();
  const { profileActions } = useActions('profile-selector');

  const getPlaceholderData = () => {
    const selectedPage = pages.find((page) => page._id === selectedPageId);

    return [
      {
        name: selectedPage?.name ?? t('insightsLite.selectPage'),
        type: selectedPage?._type,
        logoSrc: selectedPage?.picture ?? undefined,
      },
    ];
  };

  return (
    <ProfileSelector
      title={getProfileSelectorTitleByFilterOption(selectedFilterOption)}
      optionsElement={
        <InsightsPageSelectorOptions
          pages={filteredPages}
          selectedPageId={selectedPageId}
          onSelectPage={onSelectPage}
          onEditProfileClick={(pageType) =>
            socialLogin.authorize({
              pageType,
            })
          }
        />
      }
      filter={{
        searchQuery,
        selectedFilterOption,
        onSearch: setSearchQuery,
        options: sidebarFilterOptions,
        onSelectFilterOption: selectFilterOption,
      }}
      isSelectedAll={false}
      placeholderData={getPlaceholderData()}
      footer={
        <div className="tw-border-t tw-border-solid tw-border-grayscale-20 tw-p-4">
          <ActionButton
            action={profileActions.add}
            style={{ width: '100%' }}
            variant="tertiary"
            size="small"
            data-name="insights-lite_profile-management_add-profile"
          />
        </div>
      }
      dataName={{
        placeholder: 'insights-lite_profile-management_dropdown',
        options: 'insights-lite_profile-management_profileList',
        filter: 'insights-lite_profile-management_filter',
      }}
    />
  );
};

export default InsightsLitePageSelector;
